'use client';

import { useEffect, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  LoaderCircle,
  Megaphone,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  UserPlus,
  UserRound,
  X,
} from 'lucide-react';
import {
  addWikiAdministrator,
  cloneHomepageConfig,
  guideAlbumCoverCDNURL,
  guideAlbumCoverDisplayURL,
  fetchWikiAdministrators,
  lookupWikiAdministrator,
  uploadHomepageImage,
  type WikiGameProfile,
  type HomepageBenefit,
  type HomepageConfig,
  type HomepageContentLink,
  type HomepageGuideAlbum,
  type HomepageNavigationSection,
  type WikiAdministrator,
} from '../lib/homepage-config';
import { readWikiToken } from '../lib/wiki-auth';
import CatalogAdminPanel from './catalog-admin-panel';
import './home-admin-editor.css';

function wikiAdminAvatarSrc(avatar?: string) {
  const value = (avatar || '').trim();
  if (!value) return '';
  // GameKee CDN 要求 Referer，本地直链会裂图，走同源代理。
  if (/^https:\/\/cdnimg(?:-[a-z0-9]+)?\.gamekee\.com\//i.test(value)) {
    return `/api/gamekee-avatar?url=${encodeURIComponent(value)}`;
  }
  return value;
}

function AdminAvatar({ avatar, alt }: { avatar?: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  const src = wikiAdminAvatarSrc(avatar);
  if (!src || failed) return <UserRound />;
  return <img src={src} alt={alt} onError={() => setFailed(true)} />;
}

type Props = {
  gameKey: string;
  gameDomain: string;
  gameProfile: WikiGameProfile;
  config: HomepageConfig;
  open: boolean;
  defaultTab?: AdminTab;
  canEditProfile: boolean;
  canManageAdmins: boolean;
  onChange: (config: HomepageConfig) => void;
  onGameProfileChange: (profile: WikiGameProfile) => void;
  onClose: () => void;
  onSave: () => void;
};
type ListKey =
  | 'guideLinks'
  | 'promotions'
  | 'newcomerLinks'
  | 'catalogLinks'
  | 'toolLinks'
  | 'toolCards';
type AdminTab = 'home' | 'catalog' | 'guides' | 'tools' | 'benefits' | 'community';

const adminTabs: ReadonlyArray<{ id: AdminTab; label: string }> = [
  { id: 'home', label: '首页管理' },
  { id: 'catalog', label: '图鉴管理' },
  { id: 'guides', label: '攻略管理' },
  { id: 'tools', label: '工具管理' },
  { id: 'benefits', label: '福利管理' },
  { id: 'community', label: '社区管理' },
];

function CompactImageUpload({
  gameKey,
  value,
  onChange,
  disabled = false,
}: {
  gameKey: string;
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const objectUrl = useRef('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(value);

  useEffect(() => {
    if (!objectUrl.current) {
      setPreviewUrl(value ? guideAlbumCoverDisplayURL(value) : '');
      setError('');
    }
  }, [value]);

  useEffect(() => () => {
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
  }, []);

  const choose = async (file?: File) => {
    if (!file) return;
    const token = readWikiToken();
    if (!token) return setError('请重新登录');
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = URL.createObjectURL(file);
    setPreviewUrl(objectUrl.current);
    setUploading(true);
    setError('');
    try {
      // uploadHomepageImage 返回 CDN URL；保存时服务端也不再改回 OBS。
      const cdnUrl = await uploadHomepageImage(gameKey, file, token);
      onChange(guideAlbumCoverCDNURL(cdnUrl));
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = '';
      setPreviewUrl(guideAlbumCoverDisplayURL(cdnUrl));
    } catch (reason) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = '';
      setPreviewUrl(value ? guideAlbumCoverDisplayURL(value) : '');
      setError(reason instanceof Error ? reason.message : '上传失败');
    } finally {
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  };
  return (
    <div className="home-admin-compact-upload">
      <button
        type="button"
        onClick={() => input.current?.click()}
        disabled={uploading || disabled}
        aria-label={previewUrl ? '更换图片' : '上传图片'}
        title={error || '点击上传到对象存储'}
      >
        {previewUrl ? (
          <img
            className="home-admin-upload-preview"
            src={previewUrl}
            alt="已上传的图片预览"
            onError={() => {
              if (uploading || previewUrl.startsWith('blob:')) return;
              setError((current) => current || '图片地址暂时无法访问，请重新上传');
            }}
          />
        ) : (
          <ImagePlus />
        )}
        {uploading && (
          <span className="home-admin-upload-overlay">
            <LoaderCircle className="is-spinning" />
          </span>
        )}
      </button>
      <input
        ref={input}
        hidden
        type="file"
        disabled={disabled}
        accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
        onChange={(event) => choose(event.target.files?.[0])}
      />
      {error && <small>{error}</small>}
    </div>
  );
}

function HomepageBasicSettings({
  gameKey,
  gameDomain,
  gameProfile,
  onGameProfileChange,
  launchDate,
  onLaunchDateChange,
  promotions,
  onPromotionsChange,
  benefitsEnabled,
  sidebarModule,
  onSidebarModuleChange,
  canEditProfile,
  canManageAdmins,
}: {
  gameKey: string;
  gameDomain: string;
  gameProfile: WikiGameProfile;
  onGameProfileChange: (profile: WikiGameProfile) => void;
  launchDate: string;
  onLaunchDateChange: (date: string) => void;
  promotions: HomepageContentLink[];
  onPromotionsChange: (promotions: HomepageContentLink[]) => void;
  benefitsEnabled: boolean;
  sidebarModule: HomepageConfig['homeSidebarModule'];
  onSidebarModuleChange: (module: HomepageConfig['homeSidebarModule']) => void;
  canEditProfile: boolean;
  canManageAdmins: boolean;
}) {
  const [admins, setAdmins] = useState<WikiAdministrator[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [adminError, setAdminError] = useState('');
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [adminUID, setAdminUID] = useState('');
  const [adminCandidate, setAdminCandidate] = useState<WikiAdministrator | null>(null);
  const [adminSearching, setAdminSearching] = useState(false);
  const [adminAdding, setAdminAdding] = useState(false);
  useEffect(() => {
    if (!canManageAdmins) {
      setAdminsLoading(false);
      return;
    }
    const token = readWikiToken();
    if (!token) {
      setAdminsLoading(false);
      setAdminError('登录状态已失效');
      return;
    }
    let cancelled = false;
    fetchWikiAdministrators(gameKey, token)
      .then((items) => { if (!cancelled) setAdmins(items); })
      .catch((reason) => { if (!cancelled) setAdminError(reason instanceof Error ? reason.message : '管理员读取失败'); })
      .finally(() => { if (!cancelled) setAdminsLoading(false); });
    return () => { cancelled = true; };
  }, [canManageAdmins, gameKey]);
  const closeAdminDialog = () => {
    if (adminSearching || adminAdding) return;
    setAdminDialogOpen(false);
    setAdminUID('');
    setAdminCandidate(null);
    setAdminError('');
  };
  const lookupAdmin = async () => {
    const uid = adminUID.trim();
    if (!/^[1-9]\d{0,19}$/.test(uid)) {
      setAdminError('请输入正确的管理员 UID');
      setAdminCandidate(null);
      return;
    }
    const token = readWikiToken();
    if (!token) return setAdminError('登录状态已失效，请重新登录');
    setAdminSearching(true);
    setAdminError('');
    setAdminCandidate(null);
    try {
      setAdminCandidate(await lookupWikiAdministrator(gameKey, uid, token));
    } catch (reason) {
      setAdminError(reason instanceof Error ? reason.message : '用户资料查询失败');
    } finally {
      setAdminSearching(false);
    }
  };
  const confirmAdmin = async () => {
    if (!adminCandidate || adminCandidate.uid !== adminUID.trim()) return;
    const token = readWikiToken();
    if (!token) return setAdminError('登录状态已失效，请重新登录');
    setAdminAdding(true);
    setAdminError('');
    try {
      const added = await addWikiAdministrator(gameKey, adminCandidate.uid, token);
      setAdmins((items) => [...items.filter((item) => item.uid !== added.uid), added]);
      setAdminDialogOpen(false);
      setAdminUID('');
      setAdminCandidate(null);
    } catch (reason) {
      setAdminError(reason instanceof Error ? reason.message : '管理员添加失败');
    } finally {
      setAdminAdding(false);
    }
  };
  return (
    <fieldset className="home-admin-basic-settings">
      <legend>首页基础设置</legend>
      <div className="home-admin-game-profile">
        <label className="home-admin-game-icon-field">
          <span>游戏图标</span>
          <CompactImageUpload
            gameKey={gameKey}
            value={gameProfile.icon}
            disabled={!canEditProfile}
            onChange={(icon) => { if (canEditProfile) onGameProfileChange({ ...gameProfile, icon }); }}
          />
        </label>
        <label>
          <span>游戏名称</span>
          <input
            value={gameProfile.name}
            maxLength={80}
            placeholder="请输入游戏名称"
            disabled={!canEditProfile}
            onChange={(event) => onGameProfileChange({ ...gameProfile, name: event.target.value })}
          />
        </label>
        <label>
          <span>Wiki 域名</span>
          <div className="home-admin-domain-input">
            <b>/</b>
            <input
              value={gameProfile.domain}
              maxLength={32}
              placeholder="game-domain"
              disabled={!canEditProfile}
              onChange={(event) => onGameProfileChange({ ...gameProfile, domain: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
            />
          </div>
          <small>3–32 位小写字母、数字或连字符，修改后访问地址会同步变化。</small>
        </label>
      </div>
      {canManageAdmins && <div className="home-admin-manager-row">
        <span>管理员</span>
        <div className="home-admin-manager-list" aria-label="当前管理员">
          {adminsLoading && <small>读取中…</small>}
          {!adminsLoading && !admins.length && <small>暂无已添加管理员</small>}
          {admins.map((admin) => (
            <span className="home-admin-manager-avatar" title={`${admin.username || '用户'} · UID ${admin.uid}`} key={admin.uid}>
              <AdminAvatar avatar={admin.avatar} alt={admin.username || `UID ${admin.uid}`} />
            </span>
          ))}
        </div>
        <button
          className="home-admin-add-manager"
          type="button"
          onClick={() => { setAdminError(''); setAdminDialogOpen(true); }}
        >
          <UserPlus />
          添加管理员
        </button>
      </div>}
      <div className="home-admin-basic-footer">
        <label className="home-admin-date-field">
          <span>开服日期</span>
          <input
            type="date"
            value={launchDate}
            onChange={(event) => onLaunchDateChange(event.target.value)}
          />
          <small>用于自动计算首页显示的开服天数</small>
        </label>
        <a
          className="home-admin-publish-notice"
          href={`/${gameDomain}/dcommunity/create?preset=announcement`}
        >
          <Megaphone />
          发布公告
        </a>
      </div>
      <label className="home-admin-sidebar-module-setting">
        <span>首页右侧展示</span>
        <select
          value={benefitsEnabled ? sidebarModule : 'communityHot'}
          onChange={(event) =>
            onSidebarModuleChange(
              event.target.value as HomepageConfig['homeSidebarModule'],
            )
          }
          disabled={!benefitsEnabled}
        >
          {benefitsEnabled && <option value="benefits">福利专区</option>}
          <option value="communityHot">社区热帖</option>
        </select>
        <small>
          {benefitsEnabled
            ? '选择游戏公告下方展示的内容模块。'
            : '福利导航页已隐藏，此处固定展示社区热帖。'}
        </small>
      </label>
      <EditableSection
        gameKey={gameKey}
        title="宣传导航"
        hint="首页右侧内容模块下方展示的两个宣传入口。"
        items={promotions}
        onChange={onPromotionsChange}
        manageItems
        maxItems={2}
      />
      {canManageAdmins && adminDialogOpen && (
        <div className="home-admin-manager-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeAdminDialog(); }}>
          <section className="home-admin-manager-dialog" role="dialog" aria-modal="true" aria-labelledby="home-admin-manager-title">
            <header>
              <div><small>WIKI ADMIN</small><h3 id="home-admin-manager-title">添加管理员</h3></div>
              <button type="button" aria-label="关闭" onClick={closeAdminDialog}><X /></button>
            </header>
            <p>输入用户的 GameKee UID，获取并确认用户资料后即可添加。</p>
            <label htmlFor="home-admin-manager-uid">管理员 UID</label>
            <div className="home-admin-manager-search">
              <input
                id="home-admin-manager-uid"
                inputMode="numeric"
                autoFocus
                value={adminUID}
                placeholder="请输入 UID"
                onChange={(event) => { setAdminUID(event.target.value.replace(/\D/g, '')); setAdminCandidate(null); setAdminError(''); }}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void lookupAdmin(); } }}
              />
              <button type="button" onClick={lookupAdmin} disabled={adminSearching || !adminUID.trim()}>
                {adminSearching ? <><LoaderCircle className="is-spinning" />查询中</> : '获取用户信息'}
              </button>
            </div>
            {adminCandidate && (
              <div className="home-admin-manager-preview">
                <span><AdminAvatar avatar={adminCandidate.avatar} alt="" /></span>
                <div><strong>{adminCandidate.username || '未设置昵称'}</strong><small>UID {adminCandidate.uid}</small></div>
              </div>
            )}
            {adminError && <p className="home-admin-manager-error" role="alert">{adminError}</p>}
            <footer>
              <button type="button" onClick={closeAdminDialog} disabled={adminSearching || adminAdding}>取消</button>
              <button className="primary" type="button" onClick={confirmAdmin} disabled={!adminCandidate || adminAdding}>
                {adminAdding ? '添加中…' : '确认添加'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </fieldset>
  );
}

function EditableSection({
  gameKey,
  title,
  hint,
  imageLabel = '图片',
  imageRecommendation = '800×450',
  items,
  onChange,
  manageItems = false,
  maxItems = 6,
}: {
  gameKey: string;
  title: string;
  hint?: string;
  imageLabel?: string;
  imageRecommendation?: string;
  items: HomepageContentLink[];
  onChange: (items: HomepageContentLink[]) => void;
  manageItems?: boolean;
  maxItems?: number;
}) {
  const patchItem = (index: number, patch: Partial<HomepageContentLink>) =>
    onChange(
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    );
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return (
    <fieldset className="home-admin-list-section">
      <legend>{title}</legend>
      {hint && <p className="home-admin-section-hint">{hint}</p>}
      <div className={`home-admin-table${manageItems ? ' can-manage-items' : ''}`} aria-label={`${title}内容`}>
        <div className="home-admin-table-head">
          <span>标题</span>
          <span>描述</span>
          <span>链接</span>
          <span className="home-admin-image-column-title">
            {imageLabel} <small>推荐 {imageRecommendation}</small>
          </span>
          <span>{manageItems ? '操作' : '顺序'}</span>
        </div>
        {items.map((item, index) => (
          <div className="home-admin-table-row" key={`${title}-${index}`}>
            <input
              aria-label={`${title}第 ${index + 1} 项标题`}
              value={item.title}
              onChange={(event) =>
                patchItem(index, { title: event.target.value })
              }
              placeholder="输入标题"
            />
            <input
              aria-label={`${title}第 ${index + 1} 项描述`}
              value={item.description}
              onChange={(event) =>
                patchItem(index, { description: event.target.value })
              }
              placeholder="输入描述"
            />
            <input
              aria-label={`${title}第 ${index + 1} 项链接`}
              value={item.url}
              onChange={(event) =>
                patchItem(index, { url: event.target.value })
              }
              placeholder="输入链接"
            />
            <CompactImageUpload
              gameKey={gameKey}
              value={item.image}
              onChange={(image) => patchItem(index, { image })}
            />
            <span className="home-admin-order-controls">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="上移"
              >
                <ChevronUp />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === items.length - 1}
                aria-label="下移"
              >
                <ChevronDown />
              </button>
              {manageItems && (
                <button
                  className="home-admin-remove-item"
                  type="button"
                  onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
                  aria-label={`删除入口 ${item.title || index + 1}`}
                  title="删除此入口"
                >
                  <Trash2 />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
      {manageItems && items.length < maxItems && (
        <button className="home-admin-add-navigation-item" type="button" onClick={() => onChange([...items, { title: '', description: '', url: '', image: '' }])}>
          <Plus />添加入口
        </button>
      )}
    </fieldset>
  );
}

function GuideCategorySection({
  categories,
  onChange,
}: {
  categories: string[];
  onChange: (categories: string[]) => void;
}) {
  const patch = (index: number, value: string) =>
    onChange(
      categories.map((category, itemIndex) =>
        itemIndex === index ? value : category,
      ),
    );
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return (
    <fieldset className="home-admin-list-section guide-category-admin">
      <legend>攻略分类管理</legend>
      <p className="home-admin-section-hint">
        控制攻略页分类名称和显示顺序；“全部”建议保留在首位。
      </p>
      <div className="guide-category-admin-list">
        {categories.map((category, index) => (
          <div
            className="guide-category-admin-row"
            key={`guide-category-${index}`}
          >
            <input
              aria-label={`第 ${index + 1} 个攻略分类`}
              value={category}
              onChange={(event) => patch(index, event.target.value)}
              placeholder="分类名称"
            />
            <span className="home-admin-order-controls">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="上移"
              >
                <ChevronUp />
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === categories.length - 1}
                aria-label="下移"
              >
                <ChevronDown />
              </button>
            </span>
            <button
              className="guide-category-remove"
              type="button"
              disabled={categories.length === 1}
              onClick={() =>
                onChange(
                  categories.filter((_, itemIndex) => itemIndex !== index),
                )
              }
              aria-label={`删除${category}`}
            >
              <Trash2 />
            </button>
          </div>
        ))}
      </div>
      <button
        className="guide-category-add"
        type="button"
        onClick={() =>
          onChange([...categories, `新分类 ${categories.length + 1}`])
        }
      >
        ＋ 新增分类
      </button>
    </fieldset>
  );
}

type GuideAlbumPost = {
  id: string;
  title: string;
  category?: string;
  author?: string;
  cover?: string | null;
};

function GuideAlbumSection({
  gameKey,
  albums,
  onChange,
}: {
  gameKey: string;
  albums: HomepageGuideAlbum[];
  onChange: (albums: HomepageGuideAlbum[]) => void;
}) {
  const [posts, setPosts] = useState<GuideAlbumPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<HomepageGuideAlbum>({ id: '', title: '', cover: '', postIds: [] });
  const [dialogTab, setDialogTab] = useState<'manage' | 'add'>('manage');
  const [postSearch, setPostSearch] = useState('');
  const [postCategory, setPostCategory] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetch(`/api/games/${encodeURIComponent(gameKey)}/posts?type=guide&summary=1`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((value) => {
        const payload = value as { posts?: GuideAlbumPost[] };
        if (active) setPosts(Array.isArray(payload.posts) ? payload.posts : []);
      })
      .catch(() => { if (active) setPosts([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [gameKey]);

  const openCreate = () => {
    setEditingId(null);
    setDraft({ id: `guide-album-${Date.now()}`, title: '', cover: '', postIds: [] });
    setDialogTab('add');
    setPostSearch('');
    setPostCategory('');
    setDialogOpen(true);
  };
  const openEdit = (album: HomepageGuideAlbum) => {
    setEditingId(album.id);
    setDraft({ ...album, postIds: [...album.postIds] });
    setDialogTab('manage');
    setPostSearch('');
    setPostCategory('');
    setDialogOpen(true);
  };
  const movePost = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= draft.postIds.length) return;
    const postIds = [...draft.postIds];
    [postIds[index], postIds[target]] = [postIds[target], postIds[index]];
    setDraft({ ...draft, postIds });
  };
  const postCategories = Array.from(new Set(posts.map((post) => post.category?.trim()).filter((category): category is string => Boolean(category)))).sort((left, right) => left.localeCompare(right, 'zh-CN'));
  const normalizedSearch = postSearch.trim().toLocaleLowerCase('zh-CN');
  const availablePosts = posts.filter((post) =>
    !draft.postIds.includes(post.id) &&
    (!postCategory || post.category === postCategory) &&
    (!normalizedSearch || `${post.title} ${post.author || ''} ${post.category || ''}`.toLocaleLowerCase('zh-CN').includes(normalizedSearch)),
  );
  const save = () => {
    const title = draft.title.trim();
    if (!title || !draft.cover || draft.postIds.length < 2) return;
    const next = { ...draft, title };
    onChange(editingId
      ? albums.map((album) => album.id === editingId ? next : album)
      : [...albums, next]);
    setDialogOpen(false);
  };
  const remove = (album: HomepageGuideAlbum) => {
    if (window.confirm(`确定删除攻略专辑“${album.title}”吗？专辑中的攻略文章不会被删除。`)) {
      onChange(albums.filter((item) => item.id !== album.id));
    }
  };

  return (
    <fieldset className="home-admin-list-section guide-album-admin">
      <legend>攻略专辑</legend>
      <div className="guide-album-heading">
        <p className="home-admin-section-hint">把多篇已发布攻略整理成一个带名称和封面的专题模块。</p>
        <button type="button" onClick={openCreate}><Plus />新建专辑</button>
      </div>
      <div className="guide-album-list">
        {albums.map((album) => (
          <article key={album.id}>
            <span className="guide-album-cover" style={{ backgroundImage: album.cover ? `url("${guideAlbumCoverDisplayURL(album.cover).replaceAll('"', '%22')}")` : undefined }} />
            <div><strong>{album.title}</strong><small>{album.postIds.length} 篇攻略</small></div>
            <button type="button" onClick={() => openEdit(album)} aria-label={`修改${album.title}`}><Pencil /></button>
            <button className="danger" type="button" onClick={() => remove(album)} aria-label={`删除${album.title}`}><Trash2 /></button>
          </article>
        ))}
        {!albums.length && <div className="guide-album-empty">尚未创建攻略专辑</div>}
      </div>
      {dialogOpen && (
        <div className="guide-album-dialog-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDialogOpen(false); }}>
          <dialog open className="guide-album-dialog" aria-label={editingId ? '修改攻略专辑' : '新建攻略专辑'}>
            <header><div><small>GUIDE COLLECTION</small><h2>{editingId ? '修改攻略专辑' : '新建攻略专辑'}</h2></div><button type="button" onClick={() => setDialogOpen(false)} aria-label="关闭"><X /></button></header>
            <section>
              <label><span>专辑名称</span><input value={draft.title} maxLength={60} onChange={(event) => setDraft({ ...draft, title: event.target.value })} placeholder="例如：新手开荒完整指南" /></label>
              <label><span>专辑封面</span><CompactImageUpload gameKey={gameKey} value={draft.cover} onChange={(cover) => setDraft((current) => ({ ...current, cover: guideAlbumCoverCDNURL(cover) }))} /></label>
              <div className="guide-album-tabs" role="tablist" aria-label="攻略专辑内容管理">
                <button type="button" role="tab" aria-selected={dialogTab === 'manage'} className={dialogTab === 'manage' ? 'is-active' : ''} onClick={() => setDialogTab('manage')}>攻略管理 <span>{draft.postIds.length}</span></button>
                <button type="button" role="tab" aria-selected={dialogTab === 'add'} className={dialogTab === 'add' ? 'is-active' : ''} onClick={() => setDialogTab('add')}>添加攻略</button>
              </div>
              {dialogTab === 'manage' ? (
                <div className="guide-album-manage-list">
                  <div className="guide-album-pane-heading"><div><strong>专辑内攻略</strong><small>拖动顺序替代为上下按钮，删除仅会移出当前专辑。</small></div><span>至少保留 2 篇</span></div>
                  {draft.postIds.map((postId, index) => {
                    const post = posts.find((item) => item.id === postId);
                    return <article key={postId}>
                      <span className="guide-album-order">{String(index + 1).padStart(2, '0')}</span>
                      <div><strong>{post?.title || '攻略已归档或不再公开'}</strong><small>{post?.category || '攻略'}{post?.author ? ` · ${post.author}` : ''}</small></div>
                      <button type="button" onClick={() => movePost(index, -1)} disabled={index === 0} aria-label="上移攻略"><ChevronUp /></button>
                      <button type="button" onClick={() => movePost(index, 1)} disabled={index === draft.postIds.length - 1} aria-label="下移攻略"><ChevronDown /></button>
                      <button className="danger" type="button" onClick={() => setDraft({ ...draft, postIds: draft.postIds.filter((id) => id !== postId) })} aria-label="从专辑移除攻略"><Trash2 /></button>
                    </article>;
                  })}
                  {!draft.postIds.length && <p>专辑中还没有攻略，请前往“添加攻略”。</p>}
                </div>
              ) : (
                <div className="guide-album-add-pane">
                  <div className="guide-album-post-filters">
                    <label><Search /><input value={postSearch} onChange={(event) => setPostSearch(event.target.value)} placeholder="搜索攻略标题或作者" /></label>
                    <select value={postCategory} onChange={(event) => setPostCategory(event.target.value)} aria-label="按攻略类型筛选"><option value="">全部攻略类型</option>{postCategories.map((category) => <option key={category} value={category}>{category}</option>)}</select>
                  </div>
                  <div className="guide-album-post-picker">
                    <div><strong>可添加的攻略</strong><small>已加入 {draft.postIds.length} 篇</small></div>
                    {loading && <p><LoaderCircle className="is-spinning" />正在读取攻略…</p>}
                    {!loading && availablePosts.map((post) => (
                      <article key={post.id}>
                        <span><strong>{post.title}</strong><small>{post.category || '攻略'}{post.author ? ` · ${post.author}` : ''}</small></span>
                        <button type="button" onClick={() => setDraft({ ...draft, postIds: [...draft.postIds, post.id] })}><Plus />加入专辑</button>
                      </article>
                    ))}
                    {!loading && !availablePosts.length && <p>{posts.length ? '没有符合条件的可添加攻略。' : '当前还没有已发布攻略。'}</p>}
                  </div>
                </div>
              )}
            </section>
            <footer><button type="button" onClick={() => setDialogOpen(false)}>取消</button><button className="primary" type="button" disabled={!draft.title.trim() || !draft.cover || draft.postIds.length < 2} onClick={save}>保存专辑</button></footer>
          </dialog>
        </div>
      )}
    </fieldset>
  );
}

function GuideManagementPanel({
  gameKey,
  config,
  onChange,
}: {
  gameKey: string;
  config: HomepageConfig;
  onChange: (config: HomepageConfig) => void;
}) {
  return (
    <div className="guide-management-panel">
      <GuideCategorySection categories={config.guideCategories} onChange={(guideCategories) => onChange({ ...config, guideCategories })} />
      <GuideAlbumSection gameKey={gameKey} albums={config.guideAlbums} onChange={(guideAlbums) => onChange({ ...config, guideAlbums })} />
    </div>
  );
}

function formatBenefitTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value || '—';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function BenefitsAdminPanel({
  benefits,
  onChange,
}: {
  benefits: HomepageBenefit[];
  onChange: (benefits: HomepageBenefit[]) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [expiryMode, setExpiryMode] = useState<'forever' | 'timed'>('forever');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState('');
  const openDialog = () => {
    setEditingId(null);
    setCode('');
    setLabel('');
    setExpiryMode('forever');
    setExpiresAt('');
    setError('');
    setDialogOpen(true);
  };
  const openEditDialog = (benefit: HomepageBenefit) => {
    setEditingId(benefit.id);
    setCode(benefit.code);
    setLabel(benefit.label);
    setExpiryMode(benefit.expiresAt ? 'timed' : 'forever');
    if (benefit.expiresAt) {
      const date = new Date(benefit.expiresAt);
      const offset = date.getTimezoneOffset() * 60_000;
      setExpiresAt(
        Number.isNaN(date.getTime())
          ? ''
          : new Date(date.getTime() - offset).toISOString().slice(0, 16),
      );
    } else {
      setExpiresAt('');
    }
    setError('');
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);
  const addBenefit = () => {
    const normalizedCode = code.trim();
    const normalizedLabel = label.trim();
    if (!normalizedCode || !normalizedLabel) {
      setError('请填写礼包码和礼包内容。');
      return;
    }
    if (
      benefits.some(
        (benefit) =>
          benefit.id !== editingId &&
          benefit.code.trim().toLowerCase() === normalizedCode.toLowerCase(),
      )
    ) {
      setError('该礼包码已经存在。');
      return;
    }
    if (expiryMode === 'timed' && !expiresAt) {
      setError('请选择礼包码的到期时间。');
      return;
    }
    const now = new Date();
    const localDate = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('-');
    const nextBenefit: HomepageBenefit = {
      id:
        editingId ||
        (typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `benefit-${now.getTime()}`),
      code: normalizedCode,
      label: normalizedLabel,
      date: localDate,
      updatedAt: now.toISOString(),
      expiresAt:
        expiryMode === 'timed' ? new Date(expiresAt).toISOString() : null,
    };
    onChange(
      editingId
        ? benefits.map((benefit) =>
            benefit.id === editingId ? nextBenefit : benefit,
          )
        : [...benefits, nextBenefit],
    );
    closeDialog();
  };
  return (
    <section className="home-admin-benefits-panel">
      <header>
        <div>
          <strong>福利导航页</strong>
          <span>维护礼包码、礼包内容及有效期；更新时间会在添加时自动生成。</span>
        </div>
        <button type="button" onClick={openDialog}>
          <Plus />
          添加福利
        </button>
      </header>
      {benefits.length ? (
        <div className="home-admin-benefit-list">
          <div className="home-admin-benefit-list-head">
            <span>礼包码</span>
            <span>礼包内容</span>
            <span>有效期</span>
            <span>更新时间</span>
            <span>操作</span>
          </div>
          {benefits.map((benefit) => (
            <div className="home-admin-benefit-row" key={benefit.id}>
              <code>{benefit.code}</code>
              <strong>{benefit.label}</strong>
              <span>{benefit.expiresAt ? formatBenefitTime(benefit.expiresAt) : '永不过期'}</span>
              <time dateTime={benefit.updatedAt}>{formatBenefitTime(benefit.updatedAt)}</time>
              <span className="home-admin-benefit-actions">
                <button
                  type="button"
                  onClick={() => openEditDialog(benefit)}
                  aria-label={`修改礼包码 ${benefit.code}`}
                  title="修改"
                >
                  <Pencil />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(benefits.filter((item) => item.id !== benefit.id))}
                  aria-label={`删除礼包码 ${benefit.code}`}
                  title="删除"
                >
                  <Trash2 />
                </button>
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="home-admin-benefit-empty">暂无福利，点击右上角添加礼包码。</div>
      )}
      {dialogOpen && (
        <div
          className="home-admin-benefit-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeDialog();
          }}
        >
          <section
            className="home-admin-benefit-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-admin-benefit-dialog-title"
          >
            <header>
              <div>
                <small>BENEFIT CODE</small>
                <h3 id="home-admin-benefit-dialog-title">
                  {editingId ? '修改福利' : '添加福利'}
                </h3>
              </div>
              <button type="button" onClick={closeDialog} aria-label="关闭添加福利弹窗">
                <X />
              </button>
            </header>
            <div className="home-admin-benefit-form">
              <label>
                <span>礼包码</span>
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="请输入礼包码"
                  autoFocus
                />
              </label>
              <label>
                <span>礼包内容</span>
                <input
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="例如：角色自选券 × 1"
                />
              </label>
              <fieldset>
                <legend>有效期</legend>
                <div className="home-admin-benefit-expiry-options">
                  <label>
                    <input
                      type="radio"
                      name="benefit-expiry"
                      checked={expiryMode === 'forever'}
                      onChange={() => setExpiryMode('forever')}
                    />
                    永不过期
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="benefit-expiry"
                      checked={expiryMode === 'timed'}
                      onChange={() => setExpiryMode('timed')}
                    />
                    设置到期时间
                  </label>
                </div>
                {expiryMode === 'timed' && (
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(event) => setExpiresAt(event.target.value)}
                  />
                )}
              </fieldset>
              <p>更新时间将在确认添加时自动记录，无需手动填写。</p>
              {error && <p className="home-admin-benefit-error">{error}</p>}
            </div>
            <footer>
              <button type="button" onClick={closeDialog}>取消</button>
              <button className="primary" type="button" onClick={addBenefit}>
                {editingId ? '确认修改' : '确认添加'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </section>
  );
}

function NavigationModulesAdmin({
  gameKey,
  gameDomain,
  sections,
  onChange,
}: {
  gameKey: string;
  gameDomain: string;
  sections: HomepageNavigationSection[];
  onChange: (sections: HomepageNavigationSection[]) => void;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newLayout, setNewLayout] = useState<HomepageNavigationSection['layout']>('compact');
  const [deleteTarget, setDeleteTarget] = useState<HomepageNavigationSection | null>(null);
  const patchSection = (
    index: number,
    patch: Partial<HomepageNavigationSection>,
  ) =>
    onChange(
      sections.map((section, itemIndex) =>
        itemIndex === index ? { ...section, ...patch } : section,
      ),
    );
  const moveSection = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;
    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const openCreate = () => {
    setNewTitle(`导航模块 ${sections.length + 1}`);
    setNewLayout('compact');
    setCreateOpen(true);
  };
  const addSection = () => {
    const title = newTitle.trim();
    if (!title) return;
    const now = Date.now();
    const itemCount = newLayout === 'feature' ? 3 : 4;
    onChange([
      ...sections,
      {
        id: `navigation-${now}`,
        title,
        layout: newLayout,
        showDescriptions: true,
        items: Array.from({ length: itemCount }, (_, index) => ({
          title: `导航入口 ${index + 1}`,
          description: '填写入口说明',
          url: `/${gameDomain}`,
          image: '',
        })),
      },
    ]);
    setCreateOpen(false);
  };
  return (
    <div className="home-admin-custom-navigation-list">
      <div className="home-admin-navigation-intro">
        <div><strong>首页导航模块</strong><small>模块可以自由添加、删除和排序，并支持两种卡片布局。</small></div>
        <span>{sections.length} 个模块</span>
      </div>
      {sections.map((section, index) => (
        <section className="home-admin-custom-navigation" key={section.id}>
          <header>
            <label>
              <span>模块名称</span>
              <input
                value={section.title}
                onChange={(event) => patchSection(index, { title: event.target.value })}
                placeholder="输入导航模块名称"
              />
            </label>
            <label>
              <span>展示模式</span>
              <select value={section.layout} onChange={(event) => patchSection(index, { layout: event.target.value as HomepageNavigationSection['layout'] })}>
                <option value="compact">半屏小图标</option>
                <option value="feature">全屏大背景</option>
              </select>
            </label>
            <label className="home-admin-description-toggle">
              <input type="checkbox" checked={section.showDescriptions} onChange={(event) => patchSection(index, { showDescriptions: event.target.checked })} />
              <span>展示描述</span>
            </label>
            <div className="home-admin-navigation-actions">
              <button type="button" onClick={() => moveSection(index, -1)} disabled={index === 0} aria-label={`上移${section.title}`}><ChevronUp /></button>
              <button type="button" onClick={() => moveSection(index, 1)} disabled={index === sections.length - 1} aria-label={`下移${section.title}`}><ChevronDown /></button>
              <button className="danger" type="button" onClick={() => setDeleteTarget(section)} aria-label={`删除导航模块 ${section.title}`}><Trash2 /></button>
            </div>
          </header>
          <EditableSection
            gameKey={gameKey}
            title={section.layout === 'feature' ? '大背景卡片' : '小图标卡片'}
            hint={section.layout === 'feature' ? '模块独占一行，图片作为卡片大背景。' : '模块占半行，图片以方形小图标展示。'}
            imageLabel={section.layout === 'feature' ? '卡片背景图' : '图标'}
            imageRecommendation={section.layout === 'feature' ? '800×360' : '512×512'}
            items={section.items}
            onChange={(items) => patchSection(index, { items })}
            manageItems
          />
        </section>
      ))}
      <button className="home-admin-add-navigation" type="button" onClick={openCreate}><Plus />添加导航模块</button>
      {createOpen && (
        <div className="home-admin-navigation-dialog-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setCreateOpen(false); }}>
          <dialog open className="home-admin-navigation-dialog" aria-label="添加导航模块">
            <header><div><small>NAVIGATION MODULE</small><h2>添加导航模块</h2></div><button type="button" onClick={() => setCreateOpen(false)} aria-label="关闭"><X /></button></header>
            <section>
              <label><span>模块名称</span><input autoFocus maxLength={30} value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder="请输入导航模块名称" /></label>
              <fieldset><legend>展示模式</legend><div className="home-admin-navigation-mode-options">
                <button className={newLayout === 'compact' ? 'is-selected' : ''} type="button" onClick={() => setNewLayout('compact')}><span><Settings2 /></span><strong>半屏小图标</strong><small>两个模块并排，默认创建 4 个导航入口。</small></button>
                <button className={newLayout === 'feature' ? 'is-selected' : ''} type="button" onClick={() => setNewLayout('feature')}><span><ImagePlus /></span><strong>全屏大背景</strong><small>模块独占一行，默认创建 3 个导航入口。</small></button>
              </div></fieldset>
            </section>
            <footer><button type="button" onClick={() => setCreateOpen(false)}>取消</button><button className="primary" type="button" disabled={!newTitle.trim()} onClick={addSection}>确认添加</button></footer>
          </dialog>
        </div>
      )}
      {deleteTarget && (
        <div className="home-admin-navigation-dialog-layer" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDeleteTarget(null); }}>
          <dialog open className="home-admin-navigation-dialog is-confirm" aria-label="确认删除导航模块">
            <header><div><small>DELETE MODULE</small><h2>确认删除模块</h2></div><button type="button" onClick={() => setDeleteTarget(null)} aria-label="关闭"><X /></button></header>
            <section className="home-admin-navigation-delete-copy">
              <span><Trash2 /></span>
              <div><strong>确定删除“{deleteTarget.title}”吗？</strong><p>该模块将从首页移除，模块内填写的导航配置也会一并删除；对应的攻略、图鉴或工具内容不会被删除。</p></div>
            </section>
            <footer><button type="button" onClick={() => setDeleteTarget(null)}>取消</button><button className="danger" type="button" onClick={() => { onChange(sections.filter((section) => section.id !== deleteTarget.id)); setDeleteTarget(null); }}>确认删除</button></footer>
          </dialog>
        </div>
      )}
    </div>
  );
}

export default function HomeAdminEditor({
  gameKey,
  gameDomain,
  gameProfile,
  config,
  open,
  defaultTab = 'home',
  canEditProfile,
  canManageAdmins,
  onChange,
  onGameProfileChange,
  onClose,
  onSave,
}: Props) {
  const trigger = useRef<HTMLButtonElement>(null);
  const editorStart = useRef<HomepageConfig | null>(null);
  const gameProfileStart = useRef<WikiGameProfile | null>(null);
  const wasOpen = useRef(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('home');
  const [closePromptOpen, setClosePromptOpen] = useState(false);
  const updateList = (key: ListKey, value: HomepageContentLink[]) =>
    onChange({ ...config, [key]: value });
  useEffect(() => {
    if (open && !wasOpen.current) {
      editorStart.current = cloneHomepageConfig(config);
      gameProfileStart.current = { ...gameProfile };
    }
    if (!open) setClosePromptOpen(false);
    wasOpen.current = open;
  }, [config, gameProfile, open]);
  useEffect(() => {
    if (!open) return;
    const previousBodyOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
    };
  }, [open]);
  useEffect(() => {
    let frame = 0;
    const footer = document.querySelector<HTMLElement>('.shared-game-footer');
    const updatePosition = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (!trigger.current) return;
        const gap = window.innerWidth <= 520 ? 16 : 24;
        const overlap = footer
          ? Math.max(0, window.innerHeight - footer.getBoundingClientRect().top)
          : 0;
        trigger.current.style.bottom = `${gap + overlap}px`;
      });
    };
    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);
    const observer = footer ? new ResizeObserver(updatePosition) : null;
    if (footer) observer?.observe(footer);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      observer?.disconnect();
    };
  }, []);
  const requestClose = () => {
    const changed =
      (editorStart.current &&
        JSON.stringify(config) !== JSON.stringify(editorStart.current)) ||
      (gameProfileStart.current &&
        JSON.stringify(gameProfile) !== JSON.stringify(gameProfileStart.current));
    if (changed) {
      setClosePromptOpen(true);
      return;
    }
    onClose();
  };
  const discardChanges = () => {
    if (editorStart.current) {
      onChange(cloneHomepageConfig(editorStart.current));
    }
    if (gameProfileStart.current) {
      onGameProfileChange({ ...gameProfileStart.current });
    }
    setClosePromptOpen(false);
    onClose();
  };
  const saveAndClose = () => {
    setClosePromptOpen(false);
    onSave();
  };
  return (
    <>
      <button
        ref={trigger}
        className="home-admin-trigger"
        type="button"
        onClick={() => {
          if (open) requestClose();
          else {
            setActiveTab(defaultTab);
            onClose();
          }
        }}
        aria-expanded={open}
      >
        <Settings2 size={17} />
        {open ? '收起管理' : 'Wiki 管理'}
      </button>
      {open && (
        <button
          className="home-admin-backdrop"
          type="button"
          onClick={requestClose}
          aria-label="关闭首页编辑器"
        />
      )}
      <aside
        className={`home-admin-editor${open ? ' is-open' : ''}`}
        aria-label="首页编辑器"
        aria-hidden={!open}
      >
        <header>
          <div
            className="home-admin-tabs"
            role="tablist"
            aria-label="Wiki 管理模块"
          >
            {adminTabs.map(({ id, label }) => (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === id}
                className={activeTab === id ? 'is-active' : ''}
                onClick={() => setActiveTab(id)}
                key={id}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="home-admin-actions">
            <button
              type="button"
              onClick={() => window.alert('修改记录功能尚未接入。')}
            >
              修改记录
            </button>
            <button className="home-admin-save" type="button" onClick={onSave}>
              保存修改
            </button>
          </div>
        </header>
        <div className="home-admin-scroll">
          {activeTab !== 'catalog' && (
            <div className="home-admin-note">
              <span>
                {activeTab === 'home'
                  ? '管理首页基础内容和导航入口。图片点击缩略图上传，右侧按钮可调整展示顺序。'
                  : activeTab === 'guides'
                    ? '管理攻略页的分类导航；保存后写入当前游戏配置。'
                    : activeTab === 'tools'
                      ? '管理工具页卡片的标题、说明、图片和跳转链接；保存后写入当前游戏配置。'
                      : activeTab === 'benefits'
                        ? '管理福利导航页的展示状态；礼包码沿用首页福利专区的数据。'
                      : '社区内容管理独立于首页配置，后续可在这里处理帖子与评论。'}
              </span>
              {activeTab === 'tools' && (
                <label className="home-admin-visibility-switch">
                  <span>显示工具导航页</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={config.toolsEnabled}
                    onClick={() =>
                      onChange({ ...config, toolsEnabled: !config.toolsEnabled })
                    }
                  >
                    <i />
                  </button>
                </label>
              )}
              {activeTab === 'benefits' && (
                <label className="home-admin-visibility-switch">
                  <span>显示福利导航页</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={config.benefitsEnabled}
                    onClick={() =>
                      onChange({
                        ...config,
                        benefitsEnabled: !config.benefitsEnabled,
                        homeSidebarModule: config.benefitsEnabled
                          ? 'communityHot'
                          : config.homeSidebarModule,
                      })
                    }
                  >
                    <i />
                  </button>
                </label>
              )}
            </div>
          )}
          {activeTab === 'home' && (
            <>
              <HomepageBasicSettings
                gameKey={gameKey}
                gameDomain={gameDomain}
                gameProfile={gameProfile}
                onGameProfileChange={onGameProfileChange}
                launchDate={config.launchDate}
                onLaunchDateChange={(launchDate) =>
                  onChange({ ...config, launchDate })
                }
                promotions={config.promotions}
                onPromotionsChange={(promotions) =>
                  updateList('promotions', promotions)
                }
                benefitsEnabled={config.benefitsEnabled}
                sidebarModule={config.homeSidebarModule}
                canEditProfile={canEditProfile}
                canManageAdmins={canManageAdmins}
                onSidebarModuleChange={(homeSidebarModule) =>
                  onChange({ ...config, homeSidebarModule })
                }
              />
              <EditableSection
                gameKey={gameKey}
                title="版本攻略"
                hint="从上到下对应首页轮播顺序。"
                items={config.guideLinks}
                onChange={(items) => updateList('guideLinks', items)}
                manageItems
              />
              <NavigationModulesAdmin
                gameKey={gameKey}
                gameDomain={gameDomain}
                sections={config.navigationSections}
                onChange={(navigationSections) =>
                  onChange({ ...config, navigationSections })
                }
              />
            </>
          )}
          {activeTab === 'guides' && (
            <GuideManagementPanel gameKey={gameKey} config={config} onChange={onChange} />
          )}
          {activeTab === 'tools' && (
            <EditableSection
              gameKey={gameKey}
              title="工具页卡片"
              hint="配置工具页中的卡片内容和跳转链接；未填写链接的卡片不会跳转。"
              items={config.toolCards}
              onChange={(items) => updateList('toolCards', items)}
              manageItems
            />
          )}
          {activeTab === 'benefits' && (
            <BenefitsAdminPanel
              benefits={config.benefits}
              onChange={(benefits) => onChange({ ...config, benefits })}
            />
          )}
          {activeTab === 'catalog' && <CatalogAdminPanel gameKey={gameKey} />}
          {activeTab === 'community' && (
            <section className="home-admin-empty-panel">
              <strong>社区管理</strong>
              <p>本面板已预留给帖子审核、评论处理和社区内容状态管理。</p>
            </section>
          )}
        </div>
      </aside>
      {closePromptOpen && (
        <div
          className="home-admin-confirm-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setClosePromptOpen(false);
          }}
        >
          <section
            className="home-admin-confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="home-admin-confirm-title"
          >
            <h3 id="home-admin-confirm-title">保存本次修改？</h3>
            <p>当前内容尚未保存，关闭后未提交的修改将会丢失。</p>
            <div>
              <button type="button" onClick={() => setClosePromptOpen(false)}>
                继续编辑
              </button>
              <button className="danger" type="button" onClick={discardChanges}>
                放弃修改
              </button>
              <button className="primary" type="button" onClick={saveAndClose}>
                保存修改
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
